import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import Button from './ui/Button';
import Icon from './AppIcon';

/**
 * Guided chips force a specific blank so the model cannot invent details
 * (e.g. "started a new hobby" → yoga). Square-bracket placeholders must be filled.
 */
const UPDATE_CHIPS = [
  { label: 'Took a trip', text: 'Just got back from a trip to [where?]' },
  { label: 'Training / race', text: 'Training for [what race/event?]' },
  { label: 'New hobby', text: 'Started a new hobby: [which one?]' },
  { label: 'Family milestone', text: 'Had a family milestone: [what happened?]' },
  { label: 'Busy season', text: 'Been busy with [what?]' },
];

const PLACEHOLDER_RE = /\[[^\]]+\]/;

function hasUnfilledPlaceholders(text) {
  return PLACEHOLDER_RE.test(text || '');
}

function formatMonthLabel(monthYear) {
  if (!monthYear) return '';
  const [y, m] = monthYear.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString('default', { month: 'long', year: 'numeric' });
}

const MonthlyUpdatesModal = ({ onClose }) => {
  const [recentUpdates, setRecentUpdates] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewMessages, setPreviewMessages] = useState(null);
  const textareaRef = useRef(null);

  const needsSpecifics = hasUnfilledPlaceholders(recentUpdates);
  const canSave = recentUpdates.trim().length > 0 && !needsSpecifics;

  useEffect(() => {
    if (needsSpecifics && textareaRef.current) {
      // Focus so they can fill the blank immediately after tapping a chip
      textareaRef.current.focus();
    }
  }, [recentUpdates, needsSpecifics]);

  const applyChip = (chipText) => {
    setRecentUpdates(chipText);
    setError('');
    // Defer focus until after state flush
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const match = chipText.match(PLACEHOLDER_RE);
      if (match) {
        const start = chipText.indexOf(match[0]);
        const end = start + match[0].length;
        el.setSelectionRange(start, end);
      }
    });
  };

  const postUpdate = async ({ skip, text }) => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/user/recent-updates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          recentUpdates: text,
          skip: !!skip
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to save updates');
      }

      if (!skip && data.regeneratedTouchpoints?.length) {
        setPreviewMessages(data.regeneratedTouchpoints.slice(0, 3));
        return;
      }

      onClose?.();
    } catch (err) {
      console.error('[MonthlyUpdatesModal] Error:', err);
      if (skip) {
        onClose?.();
        return;
      }
      setError(err.message || 'Failed to save updates');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => postUpdate({ skip: true });

  const handleUpdate = () => {
    if (!recentUpdates.trim()) {
      handleSkip();
      return;
    }
    if (hasUnfilledPlaceholders(recentUpdates)) {
      setError('Fill in the specifics in the brackets so messages stay accurate.');
      return;
    }
    postUpdate({ skip: false, text: recentUpdates.trim() });
  };

  if (previewMessages) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
        <div className="bg-background rounded-lg shadow-xl max-w-lg w-full p-6 relative">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Check" size={28} className="text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground text-center mb-2">
            Update saved
          </h2>
          <p className="text-muted-foreground text-center mb-5 text-sm">
            Here&apos;s how upcoming touchpoints now sound with your update woven in when it fits.
          </p>
          <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
            {previewMessages.map((row) => (
              <div key={row.monthYear} className="rounded-lg border border-border bg-muted/40 p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {formatMonthLabel(row.monthYear)}
                </p>
                <p className="text-sm text-foreground">{row.message}</p>
              </div>
            ))}
          </div>
          <Button onClick={() => onClose?.()} className="w-full">
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-lg w-full p-6 relative">
        <button
          type="button"
          onClick={handleSkip}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          disabled={loading}
          aria-label="Skip"
        >
          <Icon name="X" size={20} />
        </button>

        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Sparkles" size={28} className="text-primary" />
        </div>

        <h2 className="text-2xl font-bold text-foreground text-center mb-2">
          What&apos;s been happening?
        </h2>
        <p className="text-muted-foreground text-center mb-4 text-sm">
          Tap a chip, then fill in the bracket with the real detail — we only use what you write.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {UPDATE_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => applyChip(chip.text)}
              disabled={loading}
              className="px-3 py-1.5 text-sm rounded-full border border-border bg-muted/40 hover:bg-primary/10 hover:border-primary/40 text-foreground transition-colors"
            >
              {chip.label}
            </button>
          ))}
        </div>

        <textarea
          ref={textareaRef}
          value={recentUpdates}
          onChange={(e) => {
            setRecentUpdates(e.target.value);
            if (error) setError('');
          }}
          placeholder='e.g., Started a new hobby: pottery — or tap a chip above'
          maxLength={500}
          rows={3}
          className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none mb-1"
        />
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-muted-foreground">
            {needsSpecifics
              ? 'Fill in the specifics so messages stay accurate'
              : `${recentUpdates.length}/500`}
          </p>
          {!needsSpecifics && recentUpdates.trim() && (
            <p className="text-xs text-muted-foreground">{recentUpdates.length}/500</p>
          )}
        </div>

        {error && (
          <p className="text-sm text-destructive mb-4">{error}</p>
        )}

        <div className="flex gap-3">
          <Button
            onClick={handleUpdate}
            loading={loading}
            disabled={!canSave && recentUpdates.trim().length > 0}
            className="flex-1"
          >
            <Icon name="Check" size={16} className="mr-2" />
            Save update
          </Button>
          <Button onClick={handleSkip} variant="outline" disabled={loading}>
            Not now
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          We&apos;ll ask again in about a month. Skipping won&apos;t nag you every login.
        </p>
      </div>
    </div>
  );
};

export default MonthlyUpdatesModal;
